import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Copy, Pencil, Printer, Send } from 'lucide-react';
import { Badge } from '@/components/ui/badge.jsx';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { deductibleAmount } from '@/lib/quoteFields.js';
import { formatCurrency, formatTail } from '@/lib/format.js';
import { usePeekStore } from '@/stores/peek-store.js';
import { openAircraftPeek } from '@/lib/links.js';
import { sendBlankPHF } from '@/services/deals.service.js';
import { ApiError } from '@/lib/api.js';
import Panel from './Panel.jsx';
import AircraftCoverageDialog from './AircraftCoverageDialog.jsx';
import AircraftIdentityDialog from './AircraftIdentityDialog.jsx';
import AircraftDocuments from './AircraftDocuments.jsx';
import { aircraftLabel } from '@/lib/aircraftLabel.js';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Shape: fleet entries are
//   { tail_number, make_model, year, hull_value, status, last_term_year }
// where status ∈ 'current' | 'historical'.
// dealId (optional): enables the per-aircraft blank-PHF actions. The paper PHF
// (routes/phf-print.routes.js) is aircraft-scoped with no pilot identity — the
// insured hands it to a pilot whose contact info we don't have yet. dealId ties
// the eventual submission back to this deal's session, so the actions only
// render in the deal workspace, not the account overview.
//
// coverage (optional, AIRCRAFT-CANONICALIZATION-PHASE1-SPEC 1c): Map of
// UPPERCASE tail → canonical per-term row from Supabase policy_aircraft. The
// `fleet` rows above are CRM-pipeline-derived; these are the source of truth
// the carrier request actually reads, and the two drift. Supplying it turns
// this into the fleet WRITE surface: each current aircraft renders its basis
// and deductibles, warns when hull value has no basis, and gets an Edit door.
// Absent (the account overview) → renders exactly as before.
export default function FleetPanel({ fleet, dealId, coverage, bases, editable, onCoverageSaved }) {
  const rows = fleet || [];
  const openPeek = usePeekStore((s) => s.openPeek);
  const [sendFor, setSendFor] = useState(null); // aircraft row, or null = closed
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState(null); // minted link shown for manual copy
  const [editFor, setEditFor] = useState(null); // canonical row being edited
  // Identity half of the fleet-editing surface (prompt 163 §4): the aircraft
  // whose durable facts (incl. the tail, with the question) are being edited.
  const [identityFor, setIdentityFor] = useState(null);

  const cov = (tail) => coverage?.get(String(tail || '').toUpperCase()) || null;

  // ?edit_coverage=<tail> (the runway door, DealWorkspace 'runway'): a blocker
  // about a base airport's runway lands on THIS aircraft's coverage dialog,
  // where the runway is stated, not on a panel to hunt through.
  const [searchParams, setSearchParams] = useSearchParams();
  const wantTail = searchParams.get('edit_coverage');
  useEffect(() => {
    if (!wantTail || !coverage) return;
    const row = cov(wantTail);
    if (row) setEditFor(row);
    else toast.info(`${formatTail(wantTail)} has no coverage row on this deal to edit.`);
    setSearchParams((prev) => { const n = new URLSearchParams(prev); n.delete('edit_coverage'); return n; }, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantTail, coverage]);

  const openBlankPHF = (a) => {
    const qs = new URLSearchParams({
      makeModel: a.make_model || '',
      year: a.year != null ? String(a.year) : '',
      dealId,
    });
    window.open(
      `https://ndemnifly.com/phf/print/${encodeURIComponent(a.tail_number)}?${qs}`,
      '_blank',
      'noopener'
    );
  };

  const errMsg = (err) =>
    err instanceof ApiError ? err.body?.error || err.code : err.message || 'request failed';

  // email present → mint + send; absent → mint only (link for SMS/manual).
  const mint = async (a, toEmail) => {
    setBusy(true);
    try {
      const res = await sendBlankPHF(dealId, {
        n_number: a.tail_number,
        make_model: a.make_model || '',
        year: a.year != null ? String(a.year) : '',
        email: toEmail || undefined,
      });
      if (toEmail) {
        toast.success(`Blank PHF for ${formatTail(a.tail_number)} sent to ${toEmail}`);
        setSendFor(null);
        setEmail('');
      } else {
        // Clipboard is best-effort (blocked when the tab isn't focused, or by
        // permissions) — the link renders in the dialog either way.
        setLink(res.link);
        try {
          await navigator.clipboard.writeText(res.link);
          toast.success('PHF link copied. Paste it into a text or email.');
        } catch {
          toast.info('Link ready below. Copy it manually.');
        }
      }
    } catch (err) {
      toast.error(`Blank PHF failed: ${errMsg(err)}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel
      title="Fleet"
      count={rows.length}
      empty={rows.length === 0 ? 'No aircraft on file.' : null}
    >
      <ul className="divide-y divide-border">
        {rows.map((a) => (
          <li
            key={`${a.tail_number}-${a.last_deal_id || ''}`}
            className="flex flex-wrap items-center justify-between gap-3 py-2.5"
          >
            <div className="min-w-0">
              <div className="flex items-baseline gap-2">
                {a.tail_number_id ? (
                  <button
                    type="button"
                    onClick={() => openPeek(openAircraftPeek(a.tail_number_id))}
                    className="font-mono text-sm font-semibold text-text hover:underline text-left"
                  >
                    {formatTail(a.tail_number)}
                  </button>
                ) : (
                  <span className="font-mono text-sm font-semibold text-text">
                    {formatTail(a.tail_number)}
                  </span>
                )}
                {a.status === 'historical' ? (
                  <Badge variant="muted" className="text-[9px]">
                    HISTORICAL
                  </Badge>
                ) : null}
              </div>
              <div className="mt-0.5 text-xs text-text-muted">
                {aircraftLabel(a)}
              </div>
              {/* Canonical coverage line.
                  NO BASIS REMOVED (prompt 73). The tag fired when a hull value
                  had no coverage_type, and its tooltip said a carrier "will drop
                  the hull and return no terms" — a prediction generalised from
                  one deal and one carrier, and falsified on the deal David was
                  looking at when he asked what it meant (Global and Starr both
                  rated N8095M's hull at the full $175,000 with the column blank).
                  It was also never reporting what it claimed: all twelve rows it
                  fired on carried 'Full Flight' on their own session, stated by
                  the client at intake and never copied down. A copy failure
                  wearing a carrier prophecy. The copy is done; there is nothing
                  left to warn about. */}
              {cov(a.tail_number) ? (
                <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px]">
                  {/* `outline`, NOT `muted`. The muted variant is
                      `bg-border/40 text-text-dim line-through` — it carries a
                      STRIKETHROUGH, which is right for the HISTORICAL badge
                      above it and meaningless on a coverage basis. "Full
                      Flight" has been rendering struck through on the first
                      screen of every deal, and David read it as a state he
                      could not decode. It was not a state; it was a variant
                      picked for the word "muted". A visual whose own author
                      cannot read it is not communicating. */}
                  {cov(a.tail_number).coverage_basis ? (
                    <Badge variant="outline" className="text-[9px]">
                      {cov(a.tail_number).coverage_basis}
                    </Badge>
                  ) : null}
                  {/* A STATED NIL IS AN ANSWER. This tested truthiness on TEXT
                      columns, so a stated 0 printed as a bare unformatted `0`
                      and a genuinely-unstated side printed an em-dash that
                      reads identically to a zero. 40 of 181 policy_aircraft
                      rows carry a stated zero. deductibleAmount formats it as
                      $0 and returns null ONLY for NULL / '' . */}
                  {(() => {
                    const im = deductibleAmount(cov(a.tail_number).deductible_in_motion);
                    const nim = deductibleAmount(cov(a.tail_number).deductible_not_in_motion);
                    if (im == null && nim == null) return null;
                    return (
                      <span className="text-text-dim">
                        {im != null ? `${im} IM` : 'IM not stated'} ·{' '}
                        {nim != null ? `${nim} NIM` : 'NIM not stated'}
                      </span>
                    );
                  })()}
                  {cov(a.tail_number).base_airport ? (
                    <span className="font-mono text-text-dim">{cov(a.tail_number).base_airport}</span>
                  ) : null}
                </div>
              ) : null}
            </div>
            <div className="w-24 shrink-0 text-right text-xs">
              {/* Canonical hull wins over the CRM-derived list value when we
                  have it — the panel should show what will actually be sent. */}
              {(cov(a.tail_number)?.hull_value ?? a.hull_value) ? (
                <div className="font-mono tabular-nums text-text">
                  {formatCurrency(cov(a.tail_number)?.hull_value ?? a.hull_value)}
                </div>
              ) : null}
              {a.last_term_year ? (
                <div className="text-text-dim">{a.last_term_year}</div>
              ) : null}
            </div>
            {dealId ? (
              <div className="flex shrink-0 items-center gap-1.5">
                {editable && cov(a.tail_number) ? (
                  <button
                    type="button"
                    title="Edit hull value, physical damage basis, deductibles, and base airport for this term"
                    onClick={() => setEditFor(cov(a.tail_number))}
                    className="inline-flex shrink-0 items-center gap-1 rounded-[5px] border border-border bg-surface px-2 py-1 text-[11px] text-text-muted hover:border-border-strong hover:bg-surface-strong hover:text-text"
                  >
                    <Pencil size={12} strokeWidth={1.5} />
                    Coverage
                  </button>
                ) : null}
                {editable ? (
                  <button
                    type="button"
                    title="Edit the aircraft's identity: make, model, year, seats, serial, base airport, and the tail (with the correcting-or-different question)"
                    onClick={() => setIdentityFor(a)}
                    className="inline-flex shrink-0 items-center gap-1 rounded-[5px] border border-border bg-surface px-2 py-1 text-[11px] text-text-muted hover:border-border-strong hover:bg-surface-strong hover:text-text"
                  >
                    <Pencil size={12} strokeWidth={1.5} />
                    Identity
                  </button>
                ) : null}
                <button
                  type="button"
                  title="Print a blank Pilot History Form for this aircraft, to hand to a pilot whose contact info you don't have"
                  onClick={() => openBlankPHF(a)}
                  className="inline-flex shrink-0 items-center gap-1 rounded-[5px] border border-border bg-surface px-2 py-1 text-[11px] text-text-muted hover:border-border-strong hover:bg-surface-strong hover:text-text"
                >
                  <Printer size={12} strokeWidth={1.5} />
                  Blank PHF
                </button>
                <button
                  type="button"
                  title="Email or copy a blank Pilot History Form link. The recipient can forward it to the pilot."
                  onClick={() => { setSendFor(a); setEmail(''); setLink(null); }}
                  className="inline-flex shrink-0 items-center gap-1 rounded-[5px] border border-border bg-surface px-2 py-1 text-[11px] text-text-muted hover:border-border-strong hover:bg-surface-strong hover:text-text"
                >
                  <Send size={12} strokeWidth={1.5} />
                  Send
                </button>
              </div>
            ) : null}
            {/* paperwork-1 L1b-B3 (D8): this aircraft's documents, read when asked. */}
            {a.tail_number ? <AircraftDocuments tail={a.tail_number} /> : null}
          </li>
        ))}
      </ul>

      <AircraftCoverageDialog
        dealId={dealId}
        aircraft={editFor}
        bases={bases}
        open={!!editFor}
        onOpenChange={(o) => { if (!o) setEditFor(null); }}
        onSaved={onCoverageSaved}
      />

      <AircraftIdentityDialog
        aircraft={identityFor}
        open={!!identityFor}
        onOpenChange={(o) => { if (!o) setIdentityFor(null); }}
        onSaved={onCoverageSaved}
      />

      <Dialog open={!!sendFor} onOpenChange={(o) => { if (!o) setSendFor(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>
              Send blank PHF{sendFor ? ` · ${formatTail(sendFor.tail_number)}` : ''}
            </DialogTitle>
            <DialogDescription>
              {sendFor ? aircraftLabel(sendFor, '') : ''}.
              The link isn't tied to a person. The recipient can complete it or
              forward it to the pilot.
            </DialogDescription>
          </DialogHeader>
          <div>
            <label className="mb-1 block text-xs text-text-muted">Recipient email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="owner@example.com"
              autoFocus
              className="w-full rounded-[5px] border border-border bg-surface px-2.5 py-1.5 text-sm text-text outline-none focus:border-border-strong"
            />
          </div>
          {link ? (
            <div>
              <label className="mb-1 block text-xs text-text-muted">Form link</label>
              <input
                type="text"
                readOnly
                value={link}
                onClick={(e) => e.target.select()}
                className="w-full rounded-[5px] border border-border bg-surface px-2.5 py-1.5 text-xs text-text outline-none"
              />
            </div>
          ) : null}
          <DialogFooter>
            <button
              type="button"
              disabled={busy || !sendFor}
              onClick={() => mint(sendFor, null)}
              className="inline-flex items-center gap-1.5 rounded-[5px] border border-border bg-surface px-3 py-1.5 text-[12.5px] text-text hover:border-border-strong hover:bg-surface-strong disabled:opacity-50"
            >
              <Copy size={13} strokeWidth={1.5} />
              Copy link only
            </button>
            <button
              type="button"
              disabled={busy || !sendFor || !EMAIL_RE.test(email.trim())}
              onClick={() => mint(sendFor, email.trim())}
              className="inline-flex items-center gap-1.5 rounded-[5px] border border-accent bg-accent px-3 py-1.5 text-[12.5px] text-white hover:brightness-110 disabled:opacity-50"
            >
              <Send size={13} strokeWidth={1.5} />
              {busy ? 'Sending…' : 'Send email'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Panel>
  );
}
